import React, { useState, useRef, useEffect } from "react";
import { collection, addDoc } from "firebase/firestore";
import { db } from "./firebase/config";
import { validateEmail } from "./utils/emailValidation";
import BeforeAfterComparison from "./BeforeAfterComparison";
import { analyticsService } from "./services/analytics.service";
import PrivacyConsent from "./components/PrivacyConsent";
import "./ComingSoonPage.css";

interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string) => Promise<void>;
}

const EmailModal = ({ isOpen, onClose, onSubmit }: EmailModalProps) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const validation = validateEmail(email);
    if (!validation.isValid) {
      setError(validation.error || "Please enter a valid email address");
      // Track validation error
      analyticsService.trackEvent("form_validation_error", {
        event_category: "engagement",
        event_label: "email_validation_failed",
        error_message: validation.error || "Invalid email format",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(email);
      setSuccess(true);
      // Track successful form submission
      analyticsService.trackEvent("form_submission_success", {
        event_category: "engagement",
        event_label: "waitlist_form_completed",
        form_name: "waitlist_signup",
      });

      setTimeout(() => {
        onClose();
        setEmail("");
        setSuccess(false);
      }, 2000);
    } catch (err) {
      // Show the specific error message from the backend
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to join waitlist. Please try again.";
      setError(errorMessage);

      // Track form submission error
      analyticsService.trackEvent("form_submission_error", {
        event_category: "engagement",
        event_label: "waitlist_form_failed",
        error_type: "submission_error",
        error_message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setEmail("");
      setError("");
      setSuccess(false);

      // Track modal close
      analyticsService.trackWaitlistModalClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button
          className="modal-close"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          ×
        </button>

        {success ? (
          <div className="modal-success">
            <div className="success-icon">✓</div>
            <h3>Welcome to Orindra!</h3>
            <p>
              You're on the waitlist. We'll notify you when meetings start
              thinking.
            </p>
          </div>
        ) : (
          <>
            <h3>Join the Waitlist</h3>
            <p>Be the first to experience meetings that actually work.</p>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className={error ? "error" : ""}
                  disabled={isSubmitting}
                  required
                />
                {error && <div className="error-message">{error}</div>}
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Joining..." : "Join Waitlist"}
              </button>
            </form>

            <p className="modal-note">No spam, ever. Unsubscribe anytime.</p>
          </>
        )}
      </div>
    </div>
  );
};

const ComingSoonPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const scrollIndicatorRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLElement>(null);

  // Set document title
  useEffect(() => {
    document.title = "Orindra - Meetings that think.";
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollIndicatorRef.current) {
        const scrolled = window.scrollY;
        const maxHeight =
          document.documentElement.scrollHeight - window.innerHeight;
        const scrollPercent = (scrolled / maxHeight) * 100;
        scrollIndicatorRef.current.style.transform = `scaleX(${
          scrollPercent / 100
        })`;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
        }
      });
    }, observerOptions);

    const fadeElements = document.querySelectorAll(".fade-in");
    fadeElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleJoinWaitlist = () => {
    setIsModalOpen(true);
    // Track waitlist modal open
    analyticsService.trackWaitlistModalOpen();
    analyticsService.trackButtonClick("Join Waitlist", "hero_section");
  };

  const handleEmailSubmit = async (email: string) => {
    try {
      // Track signup start
      analyticsService.trackWaitlistSignupStart();

      // Generate or get existing session ID
      let sessionId = localStorage.getItem("orindra_session_id");
      if (!sessionId) {
        sessionId =
          "session_" +
          Date.now() +
          "_" +
          Math.random().toString(36).substr(2, 9);
        localStorage.setItem("orindra_session_id", sessionId);
      }

      // Check if this email was already submitted in this session
      const submittedEmails = JSON.parse(
        localStorage.getItem("orindra_submitted_emails") || "[]"
      );
      if (submittedEmails.includes(email.toLowerCase())) {
        throw new Error(
          "You've already joined the waitlist with this email. We'll notify you when Orindra is ready!"
        );
      }

      // Check session submission count
      const sessionSubmissions = parseInt(
        localStorage.getItem("orindra_session_count") || "0"
      );
      if (sessionSubmissions >= 3) {
        throw new Error(
          "You've reached the maximum number of waitlist submissions. Please try again later."
        );
      }

      await addDoc(collection(db, "waitlist"), {
        email,
        timestamp: new Date(),
        source: "coming-soon-page",
        sessionId: sessionId,
        userAgent: navigator.userAgent,
      });

      // Track successful signup
      analyticsService.trackWaitlistSignupSuccess(email);
      analyticsService.trackFormSubmit("waitlist_form", true);

      // Update local storage to prevent duplicates
      submittedEmails.push(email.toLowerCase());
      localStorage.setItem(
        "orindra_submitted_emails",
        JSON.stringify(submittedEmails)
      );
      localStorage.setItem(
        "orindra_session_count",
        (sessionSubmissions + 1).toString()
      );
    } catch (error) {
      // Track signup error
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      analyticsService.trackWaitlistSignupError(errorMessage);
      analyticsService.trackFormSubmit("waitlist_form", false);

      throw error;
    }
  };

  return (
    <div className="coming-soon-landing-page">
      <style>{`
        /* Base styles - Mobile First */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
          line-height: 1.6;
          color: #1f2937;
          background: #fff;
          overflow-x: hidden;
        }
        .coming-soon-landing-page {
          min-height: 100vh;
          background: #fff;
        }
        .coming-soon-landing-root {
          position: relative;
        }
        .coming-soon-landing-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 16px;
        }
        .coming-soon-landing-container-header {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 16px;
        }
        .coming-soon-container-app-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
        }
        
        /* Header - Mobile First */
        .coming-soon-header-app {
          height: 70px;
          padding-bottom: 8px;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: rgba(255, 255, 255, 0.97);
          backdrop-filter: blur(20px);
          z-index: 1000;
          box-shadow: 0 2px 8px rgba(50, 98, 14, 0.04);
          border-bottom: 1px solid #f1e86d;
        }
        .coming-soon-container-app-header,
        .coming-soon-landing-container-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 16px;
        }
        .coming-soon-logo {
          display: flex;
          align-items: center;
          height: 70px;
          margin-right: 16px;
        }
        .coming-soon-logo img {
          height: 70px;
          width: auto;
          display: block;
        }
        .coming-soon-nav-links {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        @media (max-width: 768px) {
          .coming-soon-nav-links {
            gap: 0.5rem;
          }
        }
        .coming-soon-nav-link {
          color: #32620e;
          font-weight: 600;
          font-size: clamp(0.875rem, 2vw, 1rem);
          text-decoration: none;
          padding: 8px 12px;
          transition: color 0.2s, border-bottom 0.2s;
          border-bottom: 2px solid transparent;
          min-height: 44px;
          min-width: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .coming-soon-nav-link:hover {
          color: #254808;
          border-bottom: 2px solid #f1e86d;
          background-color: rgba(50, 98, 14, 0.05);
        }
        
        .coming-soon-nav-link:focus {
          outline: 2px solid #32620e;
          outline-offset: 2px;
        }
        .coming-soon-nav-cta {
          background: #32620e;
          color: white;
          padding: 10px 16px;
          border-radius: 100px;
          text-decoration: none;
          font-weight: 600;
          font-size: clamp(0.875rem, 2vw, 1rem);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: none;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          margin-left: 12px;
          box-shadow: 0 4px 20px rgba(50, 98, 14, 0.18);
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 44px;
          min-width: 44px;
        }
        .nav-cta:hover {
          background: #28510b;
          transform: translateY(-1px);
          box-shadow: 0 8px 25px rgba(50, 98, 14, 0.18);
        }
        
        .coming-soon-nav-cta:focus {
          outline: 2px solid #32620e;
          outline-offset: 2px;
        }
        .nav-cta:focus,
        .nav-cta:focus-visible {
          outline: none !important;
          box-shadow: none !important;
        }
        
        /* Responsive Header Breakpoints - Enhanced Mobile First */
        @media (min-width: 480px) {
          .coming-soon-header-app {
            height: 80px;
          }
          .coming-soon-container-app-header,
          .coming-soon-landing-container-header {
            padding: 0 20px;
          }
          .coming-soon-logo {
            height: 80px;
            margin-right: 20px;
          }
          .coming-soon-logo img {
            height: 80px;
          }
          .coming-soon-nav-links {
            gap: 16px;
          }
          .coming-soon-nav-link {
            font-size: 15px;
          }
          .coming-soon-nav-cta {
            padding: 10px 18px;
            font-size: 14px;
            margin-left: 16px;
          }
        }
        
        @media (min-width: 768px) {
          .coming-soon-header-app {
            height: 90px;
          }
          .coming-soon-container-app-header,
          .coming-soon-landing-container-header {
            padding: 0 32px;
          }
          .coming-soon-logo {
            height: 90px;
            margin-right: 24px;
          }
          .coming-soon-logo img {
            height: 90px;
          }
          .coming-soon-nav-links {
            gap: 24px;
          }
          .coming-soon-nav-link {
            font-size: 16px;
          }
          .coming-soon-nav-cta {
            padding: 12px 20px;
            font-size: 15px;
            margin-left: 20px;
          }
        }
        
        @media (min-width: 1024px) {
          .coming-soon-header-app {
            height: 100px;
          }
          .coming-soon-container-app-header,
          .coming-soon-landing-container-header {
            padding: 0 48px;
          }
          .coming-soon-logo {
            height: 100px;
            margin-right: 32px;
          }
          .coming-soon-logo img {
            height: 100px;
          }
          .coming-soon-nav-links {
            gap: 32px;
          }
          .coming-soon-nav-cta {
            padding: 12px 24px;
            margin-left: 24px;
          }
        }
        
        /* Mobile Menu for Small Screens */
        @media (max-width: 767px) {
          .coming-soon-nav-links {
            display: none;
          }
          .coming-soon-mobile-menu-toggle {
            display: flex;
            flex-direction: column;
            cursor: pointer;
            padding: 8px;
            background: none;
            border: none;
            z-index: 1001;
            min-height: 44px;
            min-width: 44px;
            justify-content: center;
            align-items: center;
          }
          .coming-soon-mobile-menu-toggle span {
            width: 24px;
            height: 3px;
            background: #32620e;
            margin: 3px 0;
            transition: all 0.3s ease;
            border-radius: 2px;
          }
          .coming-soon-mobile-menu-toggle.active span:nth-child(1) {
            transform: rotate(45deg) translate(6px, 6px);
          }
          .coming-soon-mobile-menu-toggle.active span:nth-child(2) {
            opacity: 0;
          }
          .coming-soon-mobile-menu-toggle.active span:nth-child(3) {
            transform: rotate(-45deg) translate(6px, -6px);
          }
          .coming-soon-mobile-nav {
            display: none;
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100vh;
            background: rgba(255, 255, 255, 0.98);
            backdrop-filter: blur(20px);
            z-index: 999;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 32px;
            opacity: 0;
            visibility: hidden;
            transition: all 0.3s ease;
          }
          .coming-soon-mobile-nav.open {
            display: flex;
            opacity: 1;
            visibility: visible;
          }
          .coming-soon-mobile-nav .coming-soon-nav-link {
            font-size: 24px;
            font-weight: 600;
            color: #32620e;
            text-decoration: none;
            padding: 16px 0;
            border-bottom: 3px solid transparent;
            transition: all 0.3s ease;
            min-height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
            touch-action: manipulation;
          }
          .coming-soon-mobile-nav .coming-soon-nav-link:hover {
            color: #254808;
            border-bottom: 3px solid #f1e86d;
          }
          .coming-soon-mobile-nav .coming-soon-nav-cta {
            font-size: 20px;
            padding: 18px 36px;
            margin: 16px 0;
            min-height: 44px;
            touch-action: manipulation;
          }
        }
        
        @media (min-width: 768px) {
          .coming-soon-mobile-menu-toggle {
            display: none;
          }
          .coming-soon-mobile-nav {
            display: none;
          }
        }
        
        /* Hero Section - Clean Responsive Design (emulating LandingPage.tsx) */
        .coming-soon-hero {
          text-align: center;
          padding: clamp(140px, 20vw, 200px) 0 clamp(80px, 15vw, 180px);
          position: relative;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .coming-soon-hero .coming-soon-landing-container {
          max-width: min(90vw, 1200px);
          margin: 0 auto;
          padding: 0 clamp(16px, 4vw, 48px);
        }
        
        .coming-soon-hero-headline {
          font-size: clamp(36px, 8vw, 84px);
          font-weight: 800;
          line-height: 1.05;
          margin-bottom: clamp(24px, 6vw, 30px);
          margin-top: 75px;
          letter-spacing: -0.025em;
          color: #32620e;
          opacity: 0;
          transform: translateY(30px);
          animation: fadeInUp 0.8s ease-out 0.2s forwards;
        }
        
        .coming-soon-hero-subtitle {
          font-size: clamp(18px, 3vw, 22px);
          color: #6b7280;
          margin-bottom: clamp(32px, 6vw, 48px);
          line-height: 1.5;
          opacity: 0;
          transform: translateY(30px);
          animation: fadeInUp 0.8s ease-out 0.5s forwards;
        }
        
        .coming-soon-hero-cta {
          background: #32620e;
          color: white;
          padding: clamp(16px, 3vw, 20px) clamp(32px, 5vw, 40px);
          border-radius: 100px;
          text-decoration: none;
          font-weight: 700;
          font-size: clamp(16px, 2.5vw, 18px);
          display: inline-block;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border: none;
          cursor: pointer;
          margin-bottom: 40px;
          position: relative;
          overflow: hidden;
          opacity: 0;
          transform: translateY(30px);
          animation: fadeInUp 0.8s ease-out 1.8s forwards;
          box-shadow: 0 4px 20px rgba(50, 98, 14, 0.18);
          min-height: 48px;
          touch-action: manipulation;
        }
        
        .coming-soon-hero-cta:hover {
          background: #28510b;
          transform: translateY(-3px);
          box-shadow: 0 16px 50px rgba(50, 98, 14, 0.25);
        }
        
        .coming-soon-hero-cta::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.6s;
        }
        
        .coming-soon-hero-cta:hover::before {
          left: 100%;
        }
        
        .coming-soon-hero-image-container {
          display: flex;
          justify-content: center;
          margin: clamp(32px, 6vw, 48px) 0 clamp(24px, 4vw, 32px) 0;
          opacity: 0;
          animation: fadeInUp 1.2s ease-out 1.3s forwards;
        }
        
        .coming-soon-hero-image {
          transition: all 0.3s ease;
          object-fit: contain;
          height: auto;
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
          max-width: 480px;
          width: clamp(60%, 40vw, 40%);
        }
        
        /* Dream Benefits Section - Mobile First */
        .coming-soon-dream-benefits {
          padding: 60px 0 40px;
          background: #fff;
        }
        .coming-soon-dream-headline {
          text-align: center;
          font-size: clamp(1.5rem, 3.5vw, 2.75rem);
          font-weight: 800;
          margin-bottom: 40px;
          letter-spacing: -0.03em;
          color: #1f2937;
          padding: 0 16px;
        }
        
        /* Demo Section - Mobile First */
        .coming-soon-demo {
          padding: 1px 0;
        }
        
        /* How It Works - Mobile First */
        .coming-soon-how-it-works {
          padding: clamp(60px, 8vw, 100px) 0;
          text-align: center;
          background: #f8fafc;
        }
        .coming-soon-section-headline {
          text-align: center;
          font-size: 48px;
          font-weight: 700;
          margin-bottom: 64px;
          color: #32620e;
          letter-spacing: -0.02em;
          padding: 0 16px;
        }
        .coming-soon-steps {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: clamp(40px, 6vw, 64px) 0;
          flex-wrap: nowrap;
          gap: clamp(16px, 4vw, 32px);
        }
        .coming-soon-step {
          flex: 1;
          min-width: 200px;
          padding: clamp(24px, 4vw, 40px) clamp(16px, 3vw, 24px);
          border-radius: 20px;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          transition: all 0.3s ease;
        }
        .coming-soon-step:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        }
        .coming-soon-step-icon {
          font-size: 56px;
          margin-bottom: 24px;
        }
        .coming-soon-step-title {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 12px;
          color: #32620e;
        }
        .coming-soon-step-desc {
          color: #4b5563;
          font-size: 16px;
          font-weight: 500;
        }
        .coming-soon-arrow {
          font-size: 32px;
          color: #3b82f6;
          margin: 0 16px;
          font-weight: 600;
          transform: rotate(0deg);
        }
        /* Mobile overrides to mirror LandingPage behavior */
        @media (max-width: 768px) {
          .coming-soon-steps {
            flex-direction: column;
            gap: 24px;
          }
          .coming-soon-step {
            padding: 32px 20px;
            min-width: auto;
            width: 100%;
          }
          .coming-soon-step-icon {
            font-size: 48px;
            margin-bottom: 16px;
          }
          .coming-soon-step-title {
            font-size: clamp(18px, 3vw, 20px);
            margin-bottom: 8px;
          }
          .coming-soon-step-desc {
            font-size: clamp(14px, 2.5vw, 15px);
          }
          .coming-soon-arrow {
            transform: rotate(90deg);
            margin: 12px 0;
            font-size: 24px;
          }
          
          /* Mobile footer */
          .footer {
            padding: 32px 0;
            font-size: 13px;
          }
        }
        .coming-soon-platform-card-row {
          display: flex;
          justify-content: center;
          margin-top: 40px;
          margin-bottom: 70px;
        }
        .coming-soon-platform-card {
          width: 100%;
          max-width: 1100px;
          padding: clamp(28px, 5vw, 48px);
          border-radius: 24px;
          background: linear-gradient(180deg, #ffffff 0%, #eaf0ff 100%);
          border: 1px solid #e5e7eb;
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.06);
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        .coming-soon-platform-card .coming-soon-step-title {
          font-size: clamp(20px, 3.5vw, 28px);
          color: #32620e;
          margin-top: 8px;
          margin-bottom: 8px;
        }
        .coming-soon-platform-card .coming-soon-step-desc {
          color: #4b5563;
          font-size: clamp(14px, 2.5vw, 16px);
        }
        @media (max-width: 768px) {
          .coming-soon-platform-card {
            padding: clamp(20px, 5vw, 28px);
            border-radius: 20px;
          }
          .coming-soon-step-icons img {
            height: 32px;
          }
        }
        .coming-soon-step-icons {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-bottom: 12px;
          flex-wrap: wrap;
        }
        .coming-soon-step-icons img {
          height: 32px;
          width: auto;
        }
        
        /* Final CTA - Mobile First */
        .coming-soon-final-cta {
          padding: 60px 0;
          text-align: center;
          background: linear-gradient(135deg, #f9fafb 0%, #fff 100%);
        }
        .coming-soon-final-cta-headline {
          font-size: clamp(1.5rem, 6vw, 3.5rem);
          font-weight: 900;
          line-height: 1.1;
          margin-bottom: 24px;
          color: #1f2937;
          letter-spacing: -0.03em;
          padding: 0 16px;
        }
        .coming-soon-final-cta-button {
          background: #32620e;
          color: #fff;
          border: none;
          padding: 16px 32px;
          border-radius: 100px;
          font-size: clamp(1rem, 2.5vw, 1.25rem);
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 8px 32px rgba(50, 98, 14, 0.3);
          margin-top: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          min-width: 120px;
        }
        .coming-soon-final-cta-button:hover {
          background: #2a5a0c;
          transform: translateY(-2px);
          box-shadow: 0 12px 40px rgba(50, 98, 14, 0.4);
        }
        .coming-soon-final-note {
          margin-top: 20px;
          color: #6b7280;
          font-size: clamp(0.75rem, 1.5vw, 0.875rem);
          padding: 0 16px;
        }
        
        /* Footer */
        .footer {
          background: #f9fafb;
          color: #64748b;
          font-size: 16px;
          padding: 40px 0;
          text-align: center;
          border-top: 1.5px solid #f1e86d;
          box-shadow: 0 -2px 8px rgba(50,98,14,0.04);
          letter-spacing: 0.01em;
        }
        .footer .footer-links {
          margin-top: 12px;
          display: flex;
          justify-content: center;
          gap: 32px;
        }
        .footer .footer-link {
          color: #32620e;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s;
        }
        .footer .footer-link:hover {
          color: #254808;
          text-decoration: underline;
        }
        
        /* Modal Styles - Mobile First */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          backdrop-filter: blur(4px);
          padding: 16px;
        }
        .modal-content {
          background: #fff;
          padding: 24px;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          max-width: 480px;
          width: 100%;
          position: relative;
          text-align: center;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-close {
          position: absolute;
          top: 12px;
          right: 12px;
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #6b7280;
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s;
          min-height: 44px;
          min-width: 44px;
        }
        .modal-close:hover {
          background: #f3f4f6;
          color: #1f2937;
        }
        
        .modal-close:focus {
          outline: 2px solid #667eea;
          outline-offset: 2px;
        }
        .modal-content h3 {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 12px;
          color: #1f2937;
          padding-right: 44px;
        }
        .modal-content p {
          color: #6b7280;
          margin-bottom: 24px;
          font-size: 14px;
        }
        .form-group {
          margin-bottom: 20px;
          text-align: left;
        }
        .form-group input {
          width: 100%;
          padding: 14px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 16px;
          transition: border-color 0.2s;
          min-height: 44px;
          box-sizing: border-box;
        }
        .form-group input:focus {
          outline: none;
          border-color: #32620e;
        }
        .form-group input.error {
          border-color: #ef4444;
        }
        .error-message {
          color: #ef4444;
          font-size: clamp(0.75rem, 1.5vw, 0.875rem);
          margin-top: 6px;
        }
        .submit-button {
          background: #32620e;
          color: #fff;
          border: none;
          padding: 14px 24px;
          border-radius: 8px;
          font-size: clamp(0.875rem, 2vw, 1rem);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          width: 100%;
          min-height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .submit-button:hover:not(:disabled) {
          background: #2a5a0c;
        }
        .submit-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .modal-note {
          font-size: 12px;
          color: #9ca3af;
          margin-top: 12px;
        }
        .modal-success {
          text-align: center;
        }
        .success-icon {
          width: 56px;
          height: 56px;
          background: #32620e;
          color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          font-weight: bold;
          margin: 0 auto 20px;
          box-shadow: 0 4px 20px rgba(50, 98, 14, 0.2);
        }
        .modal-success h3 {
          color: #32620e;
          margin-bottom: 12px;
          font-size: 22px;
          font-weight: 700;
        }
        
        /* Modal Responsive Breakpoints */
        @media (min-width: 480px) {
          .modal-content {
            padding: 32px;
          }
          .modal-content h3 {
            font-size: 26px;
            margin-bottom: 14px;
          }
          .modal-content p {
            font-size: 15px;
            margin-bottom: 28px;
          }
          .form-group {
            margin-bottom: 22px;
          }
          .form-group input {
            padding: 15px;
          }
          .error-message {
            font-size: 13px;
            margin-top: 7px;
          }
          .modal-note {
            font-size: 13px;
            margin-top: 14px;
          }
          .success-icon {
            width: 60px;
            height: 60px;
            font-size: 30px;
            margin-bottom: 22px;
          }
          .modal-success h3 {
            font-size: 24px;
            margin-bottom: 14px;
          }
        }
        
        @media (min-width: 768px) {
          .modal-overlay {
            padding: 20px;
          }
          .modal-content {
            padding: 40px;
          }
          .modal-close {
            top: 16px;
            right: 16px;
            font-size: 24px;
          }
          .modal-content h3 {
            font-size: 28px;
            margin-bottom: 16px;
          }
          .modal-content p {
            font-size: 16px;
            margin-bottom: 32px;
          }
          .form-group {
            margin-bottom: 24px;
          }
          .form-group input {
            padding: 16px;
          }
          .submit-button {
            padding: 16px 32px;
          }
          .error-message {
            font-size: 14px;
            margin-top: 8px;
          }
          .modal-note {
            font-size: 14px;
            margin-top: 16px;
          }
          .success-icon {
            width: 64px;
            height: 64px;
            font-size: 32px;
            margin-bottom: 24px;
          }
          .modal-success h3 {
            margin-bottom: 16px;
          }
        }
        
        @media (min-width: 1024px) {
          .modal-content {
            padding: 48px;
          }
        }
        
        /* Animations - Enhanced for Mobile */
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .coming-soon-fade-in {
          opacity: 1;
          transform: translateY(0);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .coming-soon-fade-in.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* Reduced motion for accessibility */
        @media (prefers-reduced-motion: reduce) {
          .coming-soon-fade-in {
            transition: none;
          }
          .hero-cta {
            animation: none;
            opacity: 1;
            transform: translateY(0);
          }
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
        
        /* Scroll Indicator - Mobile Optimized */
        .scroll-indicator {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 2px;
          background: #32620e;
          transform-origin: 0%;
          transform: scaleX(0);
          z-index: 1001;
        }
        
        @media (min-width: 768px) {
          .scroll-indicator {
            height: 3px;
          }
        }
        
        /* Enhanced Responsive Breakpoints */
        
        /* Small Mobile (480px+) */
        @media (min-width: 480px) {
          .coming-soon-landing-container {
            padding: 0 20px;
          }
          .coming-soon-hero {
            padding: 100px 0 70px;
          }
          .hero-image {
            width: 70%;
            max-width: 400px;
          }
          .coming-soon-hero-cta {
            padding: 16px 32px;
            font-size: 17px;
          }
          .coming-soon-final-cta-button {
            padding: 18px 36px;
            font-size: 19px;
          }
          .coming-soon-step-icons img {
            height: 36px;
          }
          .coming-soon-step-desc {
            font-size: 15px;
          }
          .coming-soon-final-note {
            font-size: 15px;
          }
        }
        
        /* Tablet (768px+) */
        @media (min-width: 768px) {
          .coming-soon-landing-container {
            padding: 0 32px;
          }
          .coming-soon-hero {
            padding: 110px 0 80px;
          }
          .hero-image {
            width: 50%;
            max-width: 450px;
          }
          .coming-soon-steps {
            flex-direction: row;
            gap: 32px;
            flex-wrap: wrap;
            justify-content: center;
          }
          .coming-soon-step {
            max-width: 280px;
            padding: 32px 24px;
          }
          .coming-soon-arrow {
            transform: rotate(0deg);
            margin: 0;
          }
          .coming-soon-step-icons {
            gap: 12px;
          }
          .coming-soon-step-icons img {
            height: 38px;
          }
          .coming-soon-step-desc {
            font-size: 16px;
          }
          .footer {
            flex-direction: row;
            justify-content: space-between;
            padding: 40px 0;
          }
          .footer .footer-links {
            gap: 24px;
          }
          .coming-soon-final-note {
            font-size: 16px;
          }
        }
        
        /* Desktop (1024px+) */
        @media (min-width: 1024px) {
          .coming-soon-landing-container {
            padding: 0 48px;
          }
          .coming-soon-hero {
            padding: 120px 0 10px;
          }
          .hero-image {
            width: 40%;
            max-width: 480px;
          }
          .coming-soon-dream-benefits {
            padding: 80px 0 60px;
          }
          .coming-soon-how-it-works {
            padding: 80px 0;
          }
          .coming-soon-steps {
            gap: 48px;
            flex-wrap: nowrap;
          }
          .coming-soon-step {
            padding: 40px 32px;
          }
          .coming-soon-step-icons img {
            height: 40px;
          }
          .coming-soon-final-cta {
            padding: 80px 0;
          }
          .coming-soon-final-cta-button {
            padding: 20px 40px;
            font-size: 20px;
          }
          .footer {
            padding: 48px 0;
          }
          .footer .footer-links {
            gap: 32px;
          }
        }
        
        /* Large Desktop (1200px+) */
        @media (min-width: 1200px) {
          .coming-soon-dream-benefits {
            padding: 96px 0 64px;
          }
          .coming-soon-final-cta {
            padding: 100px 0;
          }
          .coming-soon-final-cta-button {
            padding: 24px 48px;
            font-size: 22px;
          }
        }
        
        /* Extra Large Desktop (1440px+) */
        @media (min-width: 1440px) {
          .coming-soon-landing-container {
            max-width: 1400px;
          }
        }
        
        /* Ultra Wide (1920px+) */
        @media (min-width: 1920px) {
          .coming-soon-landing-container {
            max-width: 1600px;
          }
        }
      `}</style>

      {/* Scroll Progress Indicator */}
      <div className="scroll-indicator" ref={scrollIndicatorRef}></div>

      <div className="coming-soon-landing-root">
        {/* Header */}
        <header className="coming-soon-header-app">
          <div className="coming-soon-landing-container-header coming-soon-container-app-header">
            <a href="/" className="coming-soon-logo">
              <img src="/orindra-logo.png" alt="Orindra logo" />
            </a>
            <nav className="coming-soon-nav-links">
              <button
                className="coming-soon-nav-cta"
                onClick={handleJoinWaitlist}
                style={{
                  marginLeft: 24,
                  marginTop: 30,
                  marginBottom: 30,
                  background: "#32620e",
                  color: "#fff",
                  boxShadow: "0 4px 16px #32620e33",
                  borderColor: "#32620e",
                }}
              >
                Join Waitlist
              </button>
            </nav>

            {/* Mobile Menu Toggle */}
            <button
              className={`coming-soon-mobile-menu-toggle ${
                mobileMenuOpen ? "active" : ""
              }`}
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
                analyticsService.trackEvent("mobile_menu_toggle", {
                  event_category: "navigation",
                  event_label: mobileMenuOpen
                    ? "mobile_menu_close"
                    : "mobile_menu_open",
                });
              }}
              aria-label="Toggle mobile menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </header>

        {/* Mobile Navigation Overlay */}
        <nav
          className={`coming-soon-mobile-nav ${mobileMenuOpen ? "open" : ""}`}
        >
          <button
            className="coming-soon-nav-cta"
            onClick={() => {
              setMobileMenuOpen(false);
              handleJoinWaitlist();
            }}
          >
            Join Waitlist
          </button>
        </nav>

        {/* Hero Section */}
        <section className="coming-soon-hero" ref={heroRef}>
          <div className="coming-soon-landing-container">
            <h1 className="coming-soon-hero-headline">
              Meetings that think.
              <span
                style={{
                  display: "block",
                  width: 64,
                  height: 6,
                  background: "#f1e86d",
                  borderRadius: 3,
                  margin: "16px auto 0 auto",
                }}
              />
            </h1>
            <p className="coming-soon-hero-subtitle">
              From discussion to done.
            </p>
            <div
              className="coming-soon-hero-image-container"
              style={{
                display: "flex",
                justifyContent: "center",
                margin: "48px 0 32px 0",
                opacity: 0,
                animation: "fadeInUp 1.2s ease-out 1.3s forwards",
              }}
            >
              <img
                src="/orindra-hero.png"
                alt="Orindra Hero"
                className="coming-soon-hero-image"
                style={{
                  maxWidth: "480px",
                  width: "40%",
                  height: "auto",
                  opacity: 1,
                  animation: "none",
                }}
              />
            </div>
            <button
              className="coming-soon-hero-cta"
              onClick={handleJoinWaitlist}
            >
              Join Waitlist
            </button>
          </div>
        </section>

        {/* Dream + Benefits (Apple-like, Single Row) */}
        <section
          className="coming-soon-dream-benefits coming-soon-fade-in"
          style={{ padding: "80px 0 80px", background: "#fff" }}
        >
          <div
            className="coming-soon-landing-container"
            style={{ maxWidth: 1000 }}
          >
            <h2 className="coming-soon-dream-headline">
              Every decision. Captured.
              <span
                style={{
                  display: "block",
                  width: 64,
                  height: 6,
                  background: "#f1e86d",
                  borderRadius: 3,
                  margin: "16px auto 82px auto",
                }}
              />
            </h2>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                gap: 48,
              }}
            ></div>
          </div>
        </section>

        {/* Demo (Before/After Interactive Graphic) */}
        <section
          className="coming-soon-demo coming-soon-fade-in"
          style={{ padding: "1px 0" }}
        >
          <BeforeAfterComparison showHeader={false} />
        </section>

        {/* How It Works (Minimal, Single Row) */}
        <section
          className="coming-soon-how-it-works coming-soon-fade-in"
          style={{ marginBottom: -64 }}
        >
          <div className="coming-soon-landing-container">
            <h2 className="coming-soon-section-headline">
              The Meeting Intelligence
              <span
                style={{
                  display: "block",
                  width: 64,
                  height: 6,
                  background: "#f1e86d",
                  borderRadius: 3,
                  margin: "16px auto 0 auto",
                }}
              />
            </h2>
            <div className="coming-soon-steps">
              <div className="coming-soon-step">
                <div className="coming-soon-step-icon">💬</div>
                <h3 className="coming-soon-step-title">Speak Freely</h3>
                <p className="coming-soon-step-desc">
                  No change to your meetings.
                </p>
              </div>
              <div className="coming-soon-arrow" style={{ color: "#32620e" }}>
                →
              </div>
              <div className="coming-soon-step">
                <div className="coming-soon-step-icon">🧠</div>
                <h3 className="coming-soon-step-title">Smart Insights</h3>
                <p className="coming-soon-step-desc">
                  We capture what matters.
                </p>
              </div>
              <div className="coming-soon-arrow" style={{ color: "#32620e" }}>
                →
              </div>
              <div className="coming-soon-step">
                <div className="coming-soon-step-icon">🚀</div>
                <h3 className="coming-soon-step-title">Lead Confidently</h3>
                <p className="coming-soon-step-desc">Clarity for your team.</p>
              </div>
            </div>
            <div
              className="platform-card-row"
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 40,
                marginBottom: 70,
              }}
            >
              <div className="coming-soon-step">
                <div className="coming-soon-step-icons">
                  <img
                    src="/icons/Zoom.png"
                    alt="Zoom"
                    style={{ height: 40 }}
                  />
                  <img
                    src="/icons/Google_Meet.png"
                    alt="Google Meet"
                    style={{ height: 40 }}
                  />
                  <img
                    src="/icons/teams-logo-2.png"
                    alt="Teams"
                    style={{ height: 40 }}
                  />
                  <img
                    src="/icons/webex-logo-2.png"
                    alt="Webex"
                    style={{ height: 40 }}
                  />
                  <img
                    src="/icons/slack-logo-2.png"
                    alt="Slack"
                    style={{ height: 40 }}
                  />
                </div>
                <h3 className="coming-soon-step-title">Works Everywhere</h3>
                <p className="coming-soon-step-desc">
                  No bots joining your calls.
                </p>
              </div>
            </div>
          </div>
          <div
            className="coming-soon-landing-container"
            style={{ maxWidth: 600 }}
          >
            <blockquote
              style={{
                fontSize: 22,
                fontStyle: "italic",
                color: "#1f2937",
                textAlign: "center",
                margin: "0 auto 24px",
              }}
            >
              "I actually participate in meetings now instead of frantically
              scribbling notes. Complete game changer."
            </blockquote>
            <div
              style={{ textAlign: "center", color: "#6b7280", fontWeight: 600 }}
            >
              — Head of Product, 500-person SaaS company
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="final-cta fade-in">
          <div className="landing-container">
            <h2 className="final-cta-headline">
              Your meetings deserve better.{" "}
              <span
                style={{
                  display: "block",
                  width: 64,
                  height: 6,
                  background: "#f1e86d",
                  borderRadius: 3,
                  margin: "16px auto 0 auto",
                }}
              />
            </h2>
            <p className="final-cta-subtitle">
              Will your team be next to experience the transformation?
            </p>
            <button
              className="final-cta-button"
              onClick={() => {
                handleJoinWaitlist();
                analyticsService.trackButtonClick(
                  "Join Waitlist",
                  "final_cta_section"
                );
              }}
            >
              Join Waitlist
            </button>
            <div
              style={{
                margin: "32px 0 0 0",
                color: "#64748b",
                fontSize: 16,
                fontWeight: 500,
              }}
            >
              <span style={{ fontStyle: "italic", letterSpacing: 0.1 }}>
                "Trusted by 2,847 teams this month"
              </span>
            </div>
            <p className="final-note">
              5 meetings free • No credit card • Start in 60 seconds
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div>
            © {new Date().getFullYear()} Orindra. Built for teams who refuse to
            waste time.
          </div>
          <div className="footer-links">
            <a
              href="/privacy"
              className="footer-link"
              onClick={() =>
                analyticsService.trackEvent("footer_link_click", {
                  event_category: "navigation",
                  event_label: "privacy_policy",
                })
              }
            >
              Privacy
            </a>
            <a
              href="/terms"
              className="footer-link"
              onClick={() =>
                analyticsService.trackEvent("footer_link_click", {
                  event_category: "navigation",
                  event_label: "terms_conditions",
                })
              }
            >
              Terms
            </a>
          </div>
        </footer>
      </div>

      {/* Email Collection Modal */}
      <EmailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleEmailSubmit}
      />

      {/* Privacy Consent Banner */}
      <PrivacyConsent
        onConsentChange={(consent) => {
          analyticsService.setConsent(consent);
          // Analytics consent updated
        }}
      />
    </div>
  );
};

export default ComingSoonPage;
